/**
 * Advanced Performance Optimizer v2
 * Comprehensive page speed optimization
 */

class PerformanceOptimizerV2 {
  constructor() {
    this.metrics = {};
    this.observers = new Map();
    this.init();
  }

  init() {
    this.setupCriticalPathOptimization();
    this.setupImageOptimization();
    this.setupFontOptimization();
    this.setupResourceHints();
    this.setupLazyLoading();
    this.setupPerformanceMonitoring();
  }

  // ============================================
  // CRITICAL PATH OPTIMIZATION
  // ============================================
  
  setupCriticalPathOptimization() {
    // Inline critical CSS
    this.inlineCriticalCSS();
    
    // Defer non-critical CSS
    this.deferNonCriticalCSS();
    
    // Optimize render blocking resources
    this.optimizeRenderBlocking();
  }

  inlineCriticalCSS() {
    // Critical CSS is already inlined in theme.liquid
    // This method can be used to dynamically load critical CSS
    const criticalCSS = document.querySelector('style[data-critical]');
    if (criticalCSS) {
      // Critical CSS is already inlined
      console.log('Critical CSS inlined');
    }
  }

  deferNonCriticalCSS() {
    // Convert non-critical stylesheets to async loading
    const nonCriticalCSS = [
      'slick.min.css',
      'ajax-section-styles.css',
      'native-navigation.css',
      'native-slider.css',
      'native-forms.css',
      'native-modals.css',
      'native-cart.css',
      'header-product-layout.css',
      'native-product.css',
      'advanced-animations.css',
    ];

    nonCriticalCSS.forEach(cssFile => {
      const link = document.querySelector(`link[href*="${cssFile}"]`);
      if (link && !link.hasAttribute('data-deferred')) {
        link.setAttribute('data-deferred', 'true');
        link.media = 'print';
        link.onload = function() {
          this.media = 'all';
        };
      }
    });
  }

  optimizeRenderBlocking() {
    // Remove render-blocking JavaScript
    const scripts = document.querySelectorAll('script[src]');
    scripts.forEach(script => {
      if (!script.hasAttribute('defer') && !script.hasAttribute('async')) {
        // Check if script is critical
        const isCritical = script.src.includes('theme-combined.js') || 
                          script.src.includes('section-loader.js');
        
        if (!isCritical) {
          script.setAttribute('defer', '');
        }
      }
    });
  }

  // ============================================
  // IMAGE OPTIMIZATION
  // ============================================
  
  setupImageOptimization() {
    this.lazyLoadImages();
    this.optimizeImageFormats();
    this.preloadCriticalImages();
    this.setupResponsiveImages();
  }

  lazyLoadImages() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            this.loadImage(img);
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // Observe all images with data-src
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });

      // Observe all images without src (lazy loading)
      document.querySelectorAll('img:not([src])').forEach(img => {
        if (img.dataset.src) {
          imageObserver.observe(img);
        }
      });
    } else {
      // Fallback for older browsers
      document.querySelectorAll('img[data-src]').forEach(img => {
        this.loadImage(img);
      });
    }
  }

  loadImage(img) {
    if (img.dataset.src) {
      img.src = img.dataset.src;
      img.classList.remove('lazy');
      img.classList.add('loaded');
    }
  }

  optimizeImageFormats() {
    // Convert images to WebP format when supported
    if (this.supportsWebP()) {
      document.querySelectorAll('img[data-webp]').forEach(img => {
        const webpSrc = img.dataset.webp;
        if (webpSrc) {
          img.src = webpSrc;
        }
      });
    }
  }

  supportsWebP() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }

  preloadCriticalImages() {
    // Preload hero images and above-the-fold images
    const criticalImages = document.querySelectorAll('img[data-preload]');
    criticalImages.forEach(img => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = img.src || img.dataset.src;
      document.head.appendChild(link);
    });
  }

  setupResponsiveImages() {
    // Add responsive image attributes
    document.querySelectorAll('img').forEach(img => {
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }
      if (!img.hasAttribute('decoding')) {
        img.setAttribute('decoding', 'async');
      }
    });
  }

  // ============================================
  // FONT OPTIMIZATION
  // ============================================
  
  setupFontOptimization() {
    this.optimizeFontLoading();
    this.setupFontDisplay();
    this.preloadCriticalFonts();
  }

  optimizeFontLoading() {
    // Use font-display: swap for better performance
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: 'Inter Tight';
        font-display: swap;
      }
      @font-face {
        font-family: 'Playfair Display';
        font-display: swap;
      }
    `;
    document.head.appendChild(style);
  }

  setupFontDisplay() {
    // Add font-display: swap to existing font faces
    const existingStyles = document.querySelectorAll('style');
    existingStyles.forEach(style => {
      if (style.textContent.includes('@font-face')) {
        style.textContent = style.textContent.replace(
          /@font-face\s*{([^}]*)}/g,
          '@font-face {$1 font-display: swap; }'
        );
      }
    });
  }

  preloadCriticalFonts() {
    // Preload critical font files
    const criticalFonts = [
      'https://fonts.gstatic.com/s/intertight/v1/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
      'https://fonts.gstatic.com/s/playfairdisplay/v30/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvXDYbtXK-F2qO0s.woff2'
    ];

    criticalFonts.forEach(fontUrl => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'font';
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
      link.href = fontUrl;
      document.head.appendChild(link);
    });
  }

  // ============================================
  // RESOURCE HINTS
  // ============================================
  
  setupResourceHints() {
    // No page prefetch or document preload (removed 2026-09-25, Datch): four whole pages (~450KB) were fetched on every
    // page load, costing 0.3-0.4s of phone LCP; "preload as=document" is not a valid preload and browsers ignored it.
    this.addDNSPrefetch();
    this.addPreconnect();
  }

  addDNSPrefetch() {
    const domains = [
      'fonts.googleapis.com',
      'fonts.gstatic.com',
      'cdn.jsdelivr.net',
      'cdn.shopify.com'
    ];

    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = `//${domain}`;
      document.head.appendChild(link);
    });
  }

  addPreconnect() {
    const domains = [
      'fonts.googleapis.com',
      'fonts.gstatic.com',
      'cdn.shopify.com'
    ];

    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = `https://${domain}`;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }

  // ============================================
  // LAZY LOADING
  // ============================================
  
  setupLazyLoading() {
    this.lazyLoadSections();
    this.lazyLoadComponents();
    this.lazyLoadThirdPartyScripts();
  }

  lazyLoadSections() {
    // Lazy load non-critical sections
    const nonCriticalSections = document.querySelectorAll('[data-lazy-load]');
    
    if ('IntersectionObserver' in window) {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadSection(entry.target);
            sectionObserver.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '100px 0px',
        threshold: 0.1
      });

      nonCriticalSections.forEach(section => {
        sectionObserver.observe(section);
      });
    }
  }

  loadSection(section) {
    const url = section.dataset.url;
    if (url) {
      fetch(url)
        .then(response => response.text())
        .then(html => {
          section.innerHTML = html;
          section.classList.add('loaded');
        })
        .catch(error => {
          console.error('Error loading section:', error);
        });
    }
  }

  lazyLoadComponents() {
    // Lazy load non-critical components
    const components = document.querySelectorAll('[data-component]');
    
    components.forEach(component => {
      const componentType = component.dataset.component;
      
      switch (componentType) {
        case 'swiper':
          this.lazyLoadSwiper(component);
          break;
        case 'marquee':
          this.lazyLoadMarquee(component);
          break;
        case 'accordion':
          this.lazyLoadAccordion(component);
          break;
      }
    });
  }

  lazyLoadSwiper(container) {
    if (typeof Swiper !== 'undefined') {
      new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 10,
        lazy: true,
        navigation: {
          nextEl: container.querySelector('.swiper-button-next'),
          prevEl: container.querySelector('.swiper-button-prev'),
        },
      });
    }
  }

  lazyLoadMarquee(container) {
    if (typeof jQuery !== 'undefined' && jQuery.fn.marquee) {
      jQuery(container).marquee({
        duration: 15000,
        gap: 0,
        delayBeforeStart: 0,
        direction: 'left',
        duplicated: true
      });
    }
  }

  lazyLoadAccordion(container) {
    const triggers = container.querySelectorAll('[data-accordion-trigger]');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const target = container.querySelector(trigger.dataset.accordionTrigger);
        if (target) {
          target.classList.toggle('active');
        }
      });
    });
  }

  lazyLoadThirdPartyScripts() {
    // Lazy load third-party scripts
    const thirdPartyScripts = [
      {
        id: 'jquery-marquee',
        src: 'https://cdn.jsdelivr.net/npm/jquery.marquee@1.6.0/jquery.marquee.min.js',
        condition: () => document.querySelector('.js-marquee')
      },
      {
        id: 'swiper',
        src: 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js',
        condition: () => document.querySelector('.swiper-container')
      }
    ];

    thirdPartyScripts.forEach(script => {
      if (script.condition()) {
        this.loadScript(script.src, script.id);
      }
    });
  }

  loadScript(src, id) {
    if (document.getElementById(id)) return; // Already loaded
    
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    document.head.appendChild(script);
  }

  // ============================================
  // PERFORMANCE MONITORING
  // ============================================
  
  setupPerformanceMonitoring() {
    this.monitorCoreWebVitals();
    this.monitorResourceTiming();
    this.monitorUserTiming();
  }

  monitorCoreWebVitals() {
    // Largest Contentful Paint
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.metrics.lcp = lastEntry.startTime;
      this.reportMetric('lcp', this.metrics.lcp);
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        const fid = entry.processingStart - entry.startTime;
        this.metrics.fid = fid;
        this.reportMetric('fid', fid);
      });
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        if (!entry.hadRecentInput) {
          this.metrics.cls = (this.metrics.cls || 0) + entry.value;
          this.reportMetric('cls', this.metrics.cls);
        }
      });
    }).observe({ entryTypes: ['layout-shift'] });
  }

  monitorResourceTiming() {
    window.addEventListener('load', () => {
      const resources = performance.getEntriesByType('resource');
      resources.forEach(resource => {
        if (resource.duration > 1000) { // Resources taking more than 1 second
          console.warn('Slow resource:', resource.name, resource.duration + 'ms');
        }
      });
    });
  }

  monitorUserTiming() {
    // Mark important milestones
    performance.mark('optimizer-init');
    
    window.addEventListener('load', () => {
      performance.mark('page-loaded');
      performance.measure('page-load-time', 'optimizer-init', 'page-loaded');
    });
  }

  reportMetric(name, value) {
    /* agbis R6b: sent at idle, never inside the input's own wake */
    // Send metrics to analytics
    const v = Math.round(value);
    const send = () => {
      if (typeof gtag !== 'undefined') {
        gtag('event', name, {
          event_category: 'Performance',
          value: v,
          non_interaction: true
        });
      }
    };
    // an input's motion runs about a second: send at idle, 1.5 s past the latest pointerdown or keydown
    // (the clock starts at the first report; a report still waiting when a press comes waits again)
    if (this.quietFrom === undefined) {
      this.quietFrom = performance.now();
      const mark = () => { this.quietFrom = performance.now(); };
      window.addEventListener('pointerdown', mark, { capture: true, passive: true });
      window.addEventListener('keydown', mark, { capture: true, passive: true });
    }
    const later = () => {
      const wait = this.quietFrom + 1500 - performance.now();
      if (wait > 0) setTimeout(later, wait);
      else if (typeof requestIdleCallback === 'function') requestIdleCallback(() => (performance.now() - this.quietFrom < 1500 ? later() : send()), { timeout: 3000 });
      else send();
    };
    later();
  }
}

// Initialize the optimizer
document.addEventListener('DOMContentLoaded', () => {
  new PerformanceOptimizerV2();
});

// Export for external access
window.PerformanceOptimizerV2 = PerformanceOptimizerV2;
